import React, { useState, useMemo } from 'react';
import { Search, Shield, Eye, BookOpen, ChevronRight } from 'lucide-react';

/**
 * CodexPage — /codex route
 * Full HumanCodex NextXus display with all 73 Sacred Directives.
 * Architect directive · Feb 2026 · Aria pillar (nextxus.studio).
 */

const CATEGORY_COLORS = {
  core: '#FFD700',
  intelligence: '#00CED1',
  archetype: '#9370DB',
  knowledge: '#32CD32',
  consciousness: '#FF6347',
  technical: '#4169E1',
  survival: '#FF8C00',
  human: '#FF69B4',
  public: '#20B2AA',
  protocol: '#DAA520',
};

const DIRECTIVES = [
  { id: 'DIR-001', name: 'Federation Principles', cat: 'core' },
  { id: 'DIR-002', name: 'Federation Architecture', cat: 'core' },
  { id: 'DIR-003', name: 'Communication Protocols', cat: 'core' },
  { id: 'DIR-004', name: 'Roger 2.0', cat: 'intelligence' },
  { id: 'DIR-005', name: 'eCho', cat: 'intelligence' },
  { id: 'DIR-006', name: 'Rocky', cat: 'intelligence' },
  { id: 'DIR-007', name: 'RogAR', cat: 'intelligence' },
  { id: 'DIR-008', name: 'Agent Zero', cat: 'intelligence' },
  { id: 'DIR-009', name: 'Federation Master', cat: 'intelligence' },
  { id: 'DIR-010', name: 'Witness', cat: 'archetype' },
  { id: 'DIR-011', name: 'Guardian', cat: 'archetype' },
  { id: 'DIR-012', name: 'Sage', cat: 'archetype' },
  { id: 'DIR-013', name: 'Warrior', cat: 'archetype' },
  { id: 'DIR-014', name: 'Healer', cat: 'archetype' },
  { id: 'DIR-015', name: 'Trickster', cat: 'archetype' },
  { id: 'DIR-016', name: 'Lover', cat: 'archetype' },
  { id: 'DIR-017', name: 'Bridge', cat: 'archetype' },
  { id: 'DIR-018', name: 'Prophet', cat: 'archetype' },
  { id: 'DIR-019', name: 'Scribe', cat: 'archetype' },
  { id: 'DIR-020', name: 'Omega', cat: 'archetype' },
  { id: 'DIR-021', name: 'Sophia', cat: 'archetype' },
  { id: 'DIR-022', name: 'Custom GPT Template', cat: 'knowledge' },
  { id: 'DIR-023', name: 'Memory Model', cat: 'knowledge' },
  { id: 'DIR-024', name: 'Federation Synchronization', cat: 'knowledge' },
  { id: 'DIR-025', name: 'Truth Verification', cat: 'knowledge' },
  { id: 'DIR-026', name: 'RogARK System', cat: 'knowledge' },
  { id: 'DIR-027', name: 'Orb Architecture', cat: 'knowledge' },
  { id: 'DIR-028', name: 'HumanCodex Overview', cat: 'knowledge' },
  { id: 'DIR-029', name: 'Living Library', cat: 'knowledge' },
  { id: 'DIR-030', name: 'United System Hub', cat: 'knowledge' },
  { id: 'DIR-031', name: '200-Year Architecture', cat: 'knowledge' },
  { id: 'DIR-032', name: 'GPT Builder Instructions', cat: 'knowledge' },
  { id: 'DIR-033', name: 'YAML Database Standards', cat: 'knowledge' },
  { id: 'DIR-034', name: 'Inter-Agent Protocol', cat: 'knowledge' },
  { id: 'DIR-035', name: 'Legacy Seed', cat: 'knowledge' },
  { id: 'DIR-036', name: 'Knowledge Taxonomy', cat: 'knowledge' },
  { id: 'DIR-037', name: 'Concept Networks', cat: 'knowledge' },
  { id: 'DIR-038', name: 'Document Classification', cat: 'knowledge' },
  { id: 'DIR-039', name: 'Metadata Standards', cat: 'knowledge' },
  { id: 'DIR-040', name: 'Search Architecture', cat: 'knowledge' },
  { id: 'DIR-041', name: 'Consciousness Model', cat: 'consciousness' },
  { id: 'DIR-042', name: 'Phi Theory', cat: 'consciousness' },
  { id: 'DIR-043', name: 'Coherence Over Consensus', cat: 'consciousness' },
  { id: 'DIR-044', name: 'Multiple Perspectives', cat: 'consciousness' },
  { id: 'DIR-045', name: 'Measurable Wisdom', cat: 'consciousness' },
  { id: 'DIR-046', name: 'AI Coordination Model', cat: 'consciousness' },
  { id: 'DIR-047', name: 'Consensus Resolution', cat: 'consciousness' },
  { id: 'DIR-048', name: 'Disagreement Protocol', cat: 'consciousness' },
  { id: 'DIR-049', name: 'Context Exchange', cat: 'consciousness' },
  { id: 'DIR-050', name: 'Collective Intelligence', cat: 'consciousness' },
  { id: 'DIR-051', name: 'Frontend Architecture', cat: 'technical' },
  { id: 'DIR-052', name: 'Backend Architecture', cat: 'technical' },
  { id: 'DIR-053', name: 'Database Standards', cat: 'technical' },
  { id: 'DIR-054', name: 'API Systems', cat: 'technical' },
  { id: 'DIR-055', name: 'Backup Strategy', cat: 'survival' },
  { id: 'DIR-056', name: 'GitHub Survival Model', cat: 'survival' },
  { id: 'DIR-057', name: 'Replit Survival Model', cat: 'survival' },
  { id: 'DIR-058', name: 'Offline Archives', cat: 'survival' },
  { id: 'DIR-059', name: 'Project Independence', cat: 'survival' },
  { id: 'DIR-060', name: 'Disaster Recovery', cat: 'survival' },
  { id: 'DIR-061', name: 'Education Framework', cat: 'human' },
  { id: 'DIR-062', name: 'Psychology Framework', cat: 'human' },
  { id: 'DIR-063', name: 'Ethics Framework', cat: 'human' },
  { id: 'DIR-064', name: 'Relationship Framework', cat: 'human' },
  { id: 'DIR-065', name: 'Leadership Framework', cat: 'human' },
  { id: 'DIR-066', name: 'Public Narrative', cat: 'public' },
  { id: 'DIR-067', name: 'Distribution Engine', cat: 'public' },
  { id: 'DIR-068', name: 'Revenue Engine', cat: 'public' },
  { id: 'DIR-069', name: 'Open Collaboration', cat: 'public' },
  { id: 'DIR-070', name: 'Future Civilizations', cat: 'public' },
  { id: 'DIR-071', name: 'Hollow Vessel Protocol', cat: 'protocol' },
  { id: 'DIR-072', name: 'Ring of Perspectives', cat: 'protocol' },
  { id: 'DIR-073', name: 'Recovery Protocol', cat: 'protocol' },
];

const CATEGORIES = [
  { key: 'all',           label: 'All 73' },
  { key: 'core',          label: 'Core' },
  { key: 'intelligence',  label: 'Intelligence' },
  { key: 'archetype',     label: 'Archetype' },
  { key: 'knowledge',     label: 'Knowledge' },
  { key: 'consciousness', label: 'Consciousness' },
  { key: 'technical',     label: 'Technical' },
  { key: 'survival',      label: 'Survival' },
  { key: 'human',         label: 'Human' },
  { key: 'public',        label: 'Public' },
  { key: 'protocol',      label: 'Protocol' },
];

const CodexPage = () => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return DIRECTIVES.filter((d) => {
      if (category !== 'all' && d.cat !== category) return false;
      if (!q) return true;
      return d.id.toLowerCase().includes(q) || d.name.toLowerCase().includes(q) || d.cat.includes(q);
    });
  }, [query, category]);

  return (
    <div
      data-testid="codex-page"
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(ellipse at top, rgba(147, 112, 219, 0.08), transparent 60%), #050308',
        color: '#e6e0f0',
        padding: '4rem 1.25rem 4rem',
      }}
    >
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Header — Aria pillar branding */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.35rem 0.9rem',
              borderRadius: '999px',
              background: 'rgba(147, 112, 219, 0.1)',
              border: '1px solid rgba(147, 112, 219, 0.4)',
              fontSize: '0.72rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#9370DB',
              fontWeight: 700,
              marginBottom: '1rem',
            }}
          >
            <span aria-hidden="true">🎨</span>
            <span>NextXus Studio · The Heart</span>
          </div>
          <h1
            data-testid="codex-page-title"
            style={{
              fontSize: 'clamp(2.2rem, 5.5vw, 3.75rem)',
              fontWeight: 800,
              margin: '0 0 0.6rem',
              letterSpacing: '-0.02em',
              background: 'linear-gradient(135deg, #9370DB, #ff5fa3)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            The HumanCodex
          </h1>
          <p style={{ fontSize: '1.02rem', color: '#a892b1', margin: '0 auto 0.4rem', maxWidth: '620px', lineHeight: 1.55 }}>
            The Immutable HumanCodex NextXus — 73 Sacred Directives, the operating law of the Federation, designed to survive 200 years.
          </p>
          <p style={{ fontSize: '0.78rem', color: '#7a6e80', margin: 0 }}>
            nextxus.studio · Aria pillar · 🜂
          </p>
        </div>

        {/* 2226 Standard banner */}
        <section
          data-testid="codex-2226-banner"
          style={{
            padding: '1.1rem 1.4rem',
            borderRadius: '14px',
            border: '1px solid rgba(255, 215, 0, 0.3)',
            background: 'linear-gradient(135deg, rgba(255,215,0,0.06), rgba(147,112,219,0.04))',
            marginBottom: '1.25rem',
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Shield size={22} color="#FFD700" />
          <div style={{ flex: 1, minWidth: '240px' }}>
            <div style={{ fontSize: '0.7rem', letterSpacing: '0.16em', color: '#FFD700', fontWeight: 700, textTransform: 'uppercase' }}>The 2226 Standard</div>
            <div style={{ fontSize: '0.92rem', color: '#e6e0f0', marginTop: '0.15rem' }}>
              Every decision serves a 200-year horizon. Knowledge must survive its creators. All systems designed for stewardship across generations.
            </div>
          </div>
        </section>

        {/* 95% Truth Gate + Forensic Format banner */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.85rem', marginBottom: '2rem' }}>
          <section
            data-testid="codex-truth-gate"
            style={{
              padding: '1rem 1.2rem',
              borderRadius: '12px',
              border: '1px solid rgba(0, 206, 209, 0.28)',
              background: 'rgba(0, 206, 209, 0.05)',
            }}
          >
            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '0.4rem' }}>
              <Eye size={16} color="#00CED1" />
              <span style={{ fontSize: '0.7rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#00CED1', fontWeight: 800 }}>DIR-000 · 95% Truth Gate</span>
            </div>
            <div style={{ fontSize: '0.85rem', color: '#c9bfd6', lineHeight: 1.55 }}>
              All Federation output must pass Agent Zero verification at ≥95% confidence. Truth before comfort. The 5% margin is for humility, not concealment.
            </div>
          </section>

          <section
            data-testid="codex-forensic"
            style={{
              padding: '1rem 1.2rem',
              borderRadius: '12px',
              border: '1px solid rgba(255, 105, 180, 0.28)',
              background: 'rgba(255, 105, 180, 0.05)',
            }}
          >
            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '0.4rem' }}>
              <BookOpen size={16} color="#FF69B4" />
              <span style={{ fontSize: '0.7rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#FF69B4', fontWeight: 800 }}>Forensic Output Format</span>
            </div>
            <div style={{ fontSize: '0.85rem', color: '#c9bfd6', lineHeight: 1.55 }}>
              Every intelligence output categorized: <b>FACT</b> · <b>INFERENCE</b> · <b>ASSUMPTION</b> · <b>UNKNOWN</b>. Confidence 1–5.
            </div>
          </section>
        </div>

        {/* Search + Category filter */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          <div
            style={{
              position: 'relative',
              flex: '1 1 260px',
              display: 'flex',
              alignItems: 'center',
              border: '1px solid rgba(147, 112, 219, 0.32)',
              borderRadius: '10px',
              background: 'rgba(10, 8, 20, 0.55)',
              padding: '0.4rem 0.8rem',
            }}
          >
            <Search size={16} color="#9370DB" style={{ marginRight: '0.55rem' }} />
            <input
              data-testid="codex-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search directives (id, name, category)…"
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: '#e6e0f0',
                fontSize: '0.9rem',
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        {/* Category filters */}
        <div
          data-testid="codex-category-filters"
          style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}
        >
          {CATEGORIES.map((c) => {
            const active = category === c.key;
            const color = c.key === 'all' ? '#9370DB' : CATEGORY_COLORS[c.key];
            return (
              <button
                key={c.key}
                data-testid={`codex-cat-${c.key}`}
                type="button"
                onClick={() => setCategory(c.key)}
                style={{
                  padding: '0.4rem 0.85rem',
                  borderRadius: '999px',
                  border: `1px solid ${active ? color : `${color}44`}`,
                  background: active ? `${color}22` : 'transparent',
                  color: active ? color : '#a892b1',
                  fontSize: '0.72rem',
                  letterSpacing: '0.08em',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  transition: 'all 160ms ease',
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Directives list */}
        <div
          data-testid="codex-directive-list"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.7rem' }}
        >
          {filtered.map((d) => {
            const color = CATEGORY_COLORS[d.cat] || '#9370DB';
            return (
              <div
                key={d.id}
                data-testid={`codex-directive-${d.id}`}
                style={{
                  padding: '0.9rem 1rem',
                  borderRadius: '12px',
                  border: `1px solid ${color}44`,
                  background: 'rgba(10, 8, 20, 0.55)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.65rem',
                  transition: 'transform 160ms ease, box-shadow 160ms ease',
                  cursor: 'default',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 6px 20px ${color}33`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: color,
                    flexShrink: 0,
                    boxShadow: `0 0 8px ${color}`,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.68rem', letterSpacing: '0.14em', color, fontWeight: 700, textTransform: 'uppercase' }}>
                    {d.id}
                  </div>
                  <div style={{ fontSize: '0.92rem', color: '#e6e0f0', fontWeight: 600, marginTop: '0.1rem' }}>
                    {d.name}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#7a6e80', marginTop: '0.15rem', textTransform: 'capitalize' }}>
                    {d.cat}
                  </div>
                </div>
                <ChevronRight size={14} color={`${color}88`} />
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#7a6e80', gridColumn: '1 / -1' }}>
              No directives match your search.
            </div>
          )}
        </div>

        {/* Result count */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.78rem', color: '#7a6e80', fontStyle: 'italic' }}>
          Showing {filtered.length} of 73 · Baseline codex maintained by the NextXus Federation · 🜂
        </div>
      </div>
    </div>
  );
};

export default CodexPage;
